"use strict";
import { ApolloError } from "@apollo/client";
import { useMemo } from "react";
import { OnchainItemListOptionType } from "components/lists/items/types";
import { BRIDGED_BASE_ADDRESSES } from "constants/uniswapAddresses";
import { useTokenProjects } from "features/dataApi/tokenProjects";
import { usePersistedError } from "features/dataApi/utils";
import { areAddressesEqual } from "utils/addresses";
export function useCurrencies(currencyIds) {
  const { data: baseCurrencyInfos, loading, error, refetch } = useTokenProjects(currencyIds);
  const persistedError = usePersistedError(loading, error instanceof ApolloError ? error : void 0);
  const filteredBaseCurrencyInfos = useMemo(() => {
    return baseCurrencyInfos?.filter((currencyInfo) => {
      if (currencyInfo.currency.isNative) {
        return true;
      }
      const { address } = currencyInfo.currency;
      const bridgedAsset = BRIDGED_BASE_ADDRESSES.find((bridgedAddress) => areAddressesEqual(bridgedAddress, address));
      if (!bridgedAsset) {
        return true;
      }
      return false;
    });
  }, [baseCurrencyInfos]);
  return { data: filteredBaseCurrencyInfos, loading, error: persistedError, refetch };
}
export function currencyInfosToTokenOptions(currencyInfos) {
  return currencyInfos?.filter((cI) => Boolean(cI)).map((currencyInfo) => ({
    type: OnchainItemListOptionType.Token,
    currencyInfo,
    quantity: null,
    balanceUSD: void 0
  }));
}
export function createEmptyBalanceOption(currencyInfo) {
  return {
    type: OnchainItemListOptionType.Token,
    currencyInfo,
    balanceUSD: null,
    quantity: null
  };
}
export function useCurrencyInfosToTokenOptions({
  currencyInfos,
  portfolioBalancesById,
  sortAlphabetically
}) {
  return useMemo(() => {
    if (!currencyInfos) {
      return void 0;
    }
    const sortedCurrencyInfos = sortAlphabetically ? [...currencyInfos].sort((a, b) => {
      if (a.currency.name && b.currency.name) {
        return a.currency.name.localeCompare(b.currency.name);
      }
      return 0;
    }) : currencyInfos;
    return sortedCurrencyInfos.map((currencyInfo) => {
      const portfolioBalance = portfolioBalancesById?.[currencyInfo.currencyId.toLowerCase()];
      return portfolioBalance ? { type: OnchainItemListOptionType.Token, ...portfolioBalance } : createEmptyBalanceOption(currencyInfo);
    });
  }, [currencyInfos, portfolioBalancesById, sortAlphabetically]);
}
